import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Section, Button } from '../components/ui/Common.jsx';
import { companyInfo } from '../data.js';

export default function Contact() {
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt os | Sydthy Taxi & Turistfart ApS</title>
        <meta name="description" content="Kontakt Sydthy Taxi & Turistfart for booking af transport. Vi har afdelinger i både Bedsted Thy og Nykøbing Mors." />
      </Helmet>

      {/* Header */}
      <section className="relative pt-40 pb-24 bg-slate-900 text-white overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <h1 className="mb-6 text-5xl font-bold md:text-6xl">Kontakt os</h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Vi sidder klar ved telefonen og tasterne til at hjælpe dig med din næste transportopgave.
            </p>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-16 lg:grid-cols-2">
          {/* Contact Info & Form */}
          <div className="space-y-12">
            <div>
              <h2 className="mb-8 text-3xl font-bold">Send en besked</h2>
              {status === 'success' ? (
                <div className="bg-green-50 p-8 text-green-800 border border-green-100 flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600" />
                  <div>
                    <p className="font-bold text-lg">Tak for din besked!</p>
                    <p>Vi vender tilbage til dig hurtigst muligt.</p>
                    <Button
                      variant="outline"
                      className="mt-4 border-green-200 text-green-800 hover:bg-green-100"
                      onClick={() => setStatus('idle')}
                    >
                      Send en ny besked
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot */}
                  <div className="sr-only absolute -z-10 opacity-0">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-bold uppercase tracking-wider text-slate-500">Navn *</label>
                      <input
                        required
                        name="name"
                        type="text"
                        className="w-full border-b border-slate-200 bg-transparent py-3 outline-none transition-colors focus:border-blue-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold uppercase tracking-wider text-slate-500">Telefon *</label>
                      <input
                        required
                        name="phone"
                        type="tel"
                        className="w-full border-b border-slate-200 bg-transparent py-3 outline-none transition-colors focus:border-blue-600"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold uppercase tracking-wider text-slate-500">E-mail *</label>
                    <input
                      required
                      name="email"
                      type="email"
                      className="w-full border-b border-slate-200 bg-transparent py-3 outline-none transition-colors focus:border-blue-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold uppercase tracking-wider text-slate-500">Besked *</label>
                    <textarea
                      required
                      name="message"
                      rows="4"
                      className="w-full border-b border-slate-200 bg-transparent py-3 outline-none transition-colors focus:border-blue-600 resize-none"
                    ></textarea>
                  </div>
                  <Button
                    type="submit"
                    variant="blue"
                    size="lg"
                    className="w-full sm:w-auto gap-2"
                    disabled={status === 'submitting'}
                  >
                    {status === 'submitting' ? 'Sender...' : 'Send besked'}
                    <Send className="h-4 w-4" />
                  </Button>
                  {status === 'error' && (
                    <p className="text-red-600 text-sm">Der skete en fejl. Prøv venligst igen eller ring til os.</p>
                  )}
                </form>
              )}
            </div>

            <div className="grid gap-8 sm:grid-cols-2">
              <div className="space-y-4">
                <h3 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Ring til os</h3>
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-xl font-bold hover:text-blue-600 transition-colors">
                  <Phone className="h-5 w-5 text-blue-600" />
                  {companyInfo.phone}
                </a>
              </div>
              <div className="space-y-4">
                <h3 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Skriv til os</h3>
                <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-xl font-bold hover:text-blue-600 transition-colors">
                  <Mail className="h-5 w-5 text-blue-600" />
                  {companyInfo.email}
                </a>
              </div>
            </div>
          </div>

          {/* Locations & Maps */}
          <div className="space-y-12">
            <h2 className="text-3xl font-bold">Vores lokationer</h2>
            <div className="grid gap-8">
              {companyInfo.locations.map((loc, idx) => (
                <div key={idx} className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-blue-50 text-blue-600">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{loc.address}</h3>
                      <p className="text-slate-600">{loc.city}</p>
                    </div>
                  </div>
                  <div className="aspect-video w-full bg-slate-100">
                    <iframe
                      src={loc.mapUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Kort over ${loc.address}`}
                    ></iframe>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
